import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Receipt,
  Search,
  Filter,
  Download,
  Eye,
  QrCode,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  RefreshCw,
  Printer,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import EnhancedReceiptModal from '@/components/EnhancedReceiptModal';
import ReceiptPreviewModal from '@/components/ReceiptPreviewModal';
import { api } from '@/services/api';

interface Sale {
  id: string;
  invoiceNumber: string;
  customerName?: string;
  totalAmount: number;
  paymentMethod: string;
  status: 'completed' | 'pending' | 'voided';
  invoiceDate: string;
  cashierName: string;
  mraStatus?: 'pending' | 'submitted' | 'approved' | 'rejected' | 'failed';
  mraInvoiceId?: string;
}

interface InvoiceDto {
  id: string;
  invoiceNumber: string;
  totalAmount: number;
  paymentMethod: 'cash' | 'card' | 'mobile';
  paymentStatus: 'pending' | 'paid' | 'refunded';
  issuedAt: string;
  mraStatus?: 'pending' | 'submitted' | 'approved' | 'rejected' | 'failed';
  mraInvoiceId?: string;
}

const SalesHistoryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [receiptModal, setReceiptModal] = useState<{
    isOpen: boolean;
    invoiceId: string;
    invoiceNumber: string;
  }>({
    isOpen: false,
    invoiceId: '',
    invoiceNumber: '',
  });
  const [showEnhancedReceiptModal, setShowEnhancedReceiptModal] = useState(false);

  const { data: invoices = [], isLoading, isError } = useQuery<InvoiceDto[]>({
    queryKey: ['sales', 'invoices'],
    queryFn: async () => {
      const res = await api.get('/api/sales');
      return (res.data?.data?.invoices || res.data?.invoices || []) as InvoiceDto[];
    },
  });

  const submitMRAMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const res = await api.post(`/api/sales/invoices/${invoiceId}/submit-mra`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'invoices'] });
      toast.success('Invoice submitted to MRA successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to submit to MRA');
    },
  });

  const handleMRASubmit = (invoiceId: string) => {
    submitMRAMutation.mutate(invoiceId);
  };

  const handleReceiptPreview = (invoiceId: string, invoiceNumber: string) => {
    setReceiptModal({
      isOpen: true,
      invoiceId,
      invoiceNumber,
    });
  };

  const sales: Sale[] = useMemo(() => {
    return invoices
      .slice()
      .reverse()
      .map((inv: InvoiceDto) => {
        const status: Sale['status'] = inv.paymentStatus === 'paid' ? 'completed' : 'pending';
        return {
          id: inv.id,
          invoiceNumber: inv.invoiceNumber,
          customerName: undefined,
          totalAmount: inv.totalAmount,
          paymentMethod: inv.paymentMethod,
          status,
          invoiceDate: inv.issuedAt,
          cashierName: 'admin',
          mraStatus: inv.mraStatus || 'pending',
          mraInvoiceId: inv.mraInvoiceId,
        };
      });
  }, [invoices]);

  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sale.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-MW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'voided':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-700 bg-green-100';
      case 'pending':
        return 'text-yellow-700 bg-yellow-100';
      case 'voided':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getMRAStatusIcon = (mraStatus?: string) => {
    switch (mraStatus) {
      case 'approved':
        return <Shield className="h-4 w-4 text-green-500" />;
      case 'submitted':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'rejected':
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getMRAStatusColor = (mraStatus?: string) => {
    switch (mraStatus) {
      case 'approved':
        return 'text-green-700 bg-green-100';
      case 'submitted':
        return 'text-blue-700 bg-blue-100';
      case 'rejected':
        return 'text-red-700 bg-red-100';
      case 'failed':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Sales History
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View and manage your sales transactions
          </p>
        </div>
        <Button className="flex items-center">
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by invoice number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="voided">Voided</option>
              </select>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="all">All Time</option>
              </select>
              <Button variant="ghost" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MRA Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr>
                    <td className="px-6 py-6 text-sm text-gray-600" colSpan={8}>
                      Loading sales...
                    </td>
                  </tr>
                )}
                {isError && (
                  <tr>
                    <td className="px-6 py-6 text-sm text-red-600" colSpan={8}>
                      Failed to load sales
                    </td>
                  </tr>
                )}
                {filteredSales.map((sale, index) => (
                  <motion.tr
                    key={sale.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sale.invoiceNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sale.customerName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(sale.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${
                        sale.paymentMethod === 'cash' ? 'bg-green-100 text-green-800' :
                        sale.paymentMethod === 'card' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {sale.paymentMethod.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(sale.status)}
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sale.status)}`}>
                          {sale.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getMRAStatusIcon(sale.mraStatus)}
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMRAStatusColor(sale.mraStatus)}`}>
                          {sale.mraStatus || 'pending'}
                        </span>
                      </div>
                      {sale.mraInvoiceId && (
                        <div className="text-xs text-gray-500 mt-1">
                          ID: {sale.mraInvoiceId}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(sale.invoiceDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            setReceiptModal({
                              isOpen: true,
                              invoiceId: sale.id,
                              invoiceNumber: sale.invoiceNumber
                            });
                          }}
                          title="View Receipt"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            setShowEnhancedReceiptModal(true);
                            setReceiptModal({
                              isOpen: true,
                              invoiceId: sale.id,
                              invoiceNumber: sale.invoiceNumber
                            });
                          }}
                          title="Enhanced Receipt with QR Code"
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleReceiptPreview(sale.id, sale.invoiceNumber)}
                          title="Print Receipt"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        {sale.mraStatus !== 'approved' && sale.mraStatus !== 'submitted' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleMRASubmit(sale.id)}
                            disabled={submitMRAMutation.isPending}
                            title="Submit to MRA"
                          >
                            <RefreshCw className={`h-4 w-4 ${submitMRAMutation.isPending ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredSales.length === 0 && (
        <div className="text-center py-12">
          <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No sales found
          </h3>
          <p className="text-gray-600">
            {searchTerm 
              ? 'No sales match your search criteria.'
              : 'No sales recorded for the selected period.'
            }
          </p>
        </div>
      )}
      
      {/* Receipt Preview Modal */}
      <ReceiptPreviewModal
        isOpen={receiptModal.isOpen}
        onClose={() => setReceiptModal({ ...receiptModal, isOpen: false })}
        invoiceId={receiptModal.invoiceId}
        invoiceNumber={receiptModal.invoiceNumber}
      />

      {/* Enhanced Receipt Modal */}
      <EnhancedReceiptModal
        isOpen={showEnhancedReceiptModal}
        onClose={() => {
          setShowEnhancedReceiptModal(false);
          setReceiptModal({ ...receiptModal, isOpen: false });
        }}
        invoiceId={receiptModal.invoiceId}
        invoiceNumber={receiptModal.invoiceNumber}
      />
    </div>
  );
};

export default SalesHistoryPage;
